/**
 * THE TIME BRACKET — one vocabulary, one store, five surfaces.
 *
 * 24H · 7D · 30D · 90D · YTD · All · Custom
 *
 * All-time exists. It was removed once, on the strength of a real incident:
 * the old dashboard asked an event stream a population question and shipped
 * unbounded rows to the client. That is an engineering failure and this
 * architecture already fixes it — the console reads a SQLite index whose
 * composite covering indexes are tenant-leading, so a bracketed range scan
 * and an unbracketed one are the same plan with the same LIMIT and the same
 * keyset cursor. `All` is the ABSENCE of both bounds, it is bounded by the
 * page cap like every other read, and it costs nothing extra.
 *
 * THREE RULES THIS MODULE ENFORCES
 *
 *  1. THE SERVER NEVER RECEIVES A PRESET NAME. It receives `since` / `until`
 *     as explicit ISO instants, or neither. YTD and Custom are questions
 *     about the reader's DISPLAY timezone — a client-local preference the
 *     server has never been told — so resolving them here is what keeps the
 *     boundary auditable in the URL instead of guessed at in SQL.
 *  2. `until` IS LEFT OPEN on every rolling preset. The range keeps tracking
 *     the present, and an open upper bound is what makes the read ineligible
 *     for the immutable sealed-window ETag, which is already the server's
 *     correct behaviour.
 *  3. THE RESOLVED INSTANTS ARE THE CACHE KEY, never the label. A custom
 *     range that lands on the same instants as a preset shares the entry,
 *     which is the same rule the server's ETag follows.
 *
 * Not React context: the control mounts on five surfaces and a provider in
 * App.tsx would re-render the shell on every bracket change. A module-level
 * store with `useSyncExternalStore` re-renders exactly its consumers.
 */

import { useSyncExternalStore } from "react";

import { setParams } from "./route";
import {
  displayTz,
  fromLocalInput,
  onTzModeChange,
  shortStamp,
  tzMode,
  zone,
  zonedWallToUtc,
  zonedYear,
} from "./time";

/* ------------------------------------------------------------ vocabulary */

export const BRACKETS = ["24h", "7d", "30d", "90d", "ytd", "all"] as const;
export type Preset = (typeof BRACKETS)[number];
export type Bracket = Preset | "custom";

export const BRACKET_LABEL: Record<Bracket, string> = {
  "24h": "24H",
  "7d": "7D",
  "30d": "30D",
  "90d": "90D",
  ytd: "YTD",
  all: "All",
  custom: "Custom",
};

/** All-time, because the console reads all-time on both surfaces today and a
 *  narrower default would show the operator LESS than before, right after he
 *  asked for more. */
export const DEFAULT_BRACKET: Bracket = "all";

const PRESET_DAYS: Record<Exclude<Preset, "ytd" | "all">, number> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

/** A rolling lower bound recomputed on every render would mint a new
 *  react-query key every millisecond. Quantising `now` to the minute keeps
 *  the key stable between refetches (the fastest cadence is 30 s) while the
 *  window still follows the clock. */
const QUANTUM_MS = 60_000;

export interface Range {
  since: string | null;
  until: string | null;
  /** The resolved range in words, for the reader. Never a request parameter
   *  on its own — see `scopeParam`. */
  label: string;
  bracket: Bracket;
}

export function isBracket(v: unknown): v is Bracket {
  return typeof v === "string" && (v === "custom" || (BRACKETS as readonly string[]).includes(v));
}

/* ------------------------------------------------------------ resolution */

/**
 * PRECEDENCE, ported verbatim from the server's `parse_window()`: an explicit
 * from/to WINS OUTRIGHT and the preset is ignored entirely; else the preset;
 * else the default. An unknown bracket falls back to the default rather than
 * throwing — a stale URL from an older build must not blank the console.
 */
export function resolveBracket(
  b: Bracket | string | null | undefined,
  from: string | null = null,
  to: string | null = null,
  now: Date = new Date(),
): Range {
  const mode = tzMode();

  if (from || to) {
    const zoneWord = zone(from ?? to ?? now, mode);
    const left = from ? shortStamp(from, mode) : "earliest";
    const right = to ? shortStamp(to, mode) : "now";
    return { since: from, until: to, label: `${left} → ${right} ${zoneWord}`, bracket: "custom" };
  }

  const bracket: Bracket = isBracket(b) && b !== "custom" ? b : DEFAULT_BRACKET;

  if (bracket === "all" || bracket === "custom") {
    return { since: null, until: null, label: "All · no bounds", bracket: "all" };
  }

  // THE BOUNDARY IS SHOWN, NOT IMPLIED. Echoing "24H" beside a pill that
  // already says 24H is one fact printed twice; echoing the instant it
  // resolved to is the thing the reader cannot otherwise see — and for YTD it
  // is how they watch the LOCAL/UTC toggle move the new year by an hour.
  const since =
    bracket === "ytd"
      ? zonedWallToUtc(zonedYear(now, mode), 1, 1, 0, 0, 0, mode).toISOString()
      : new Date(
          Math.floor(now.getTime() / QUANTUM_MS) * QUANTUM_MS - PRESET_DAYS[bracket] * 86_400_000,
        ).toISOString();

  return {
    since,
    until: null,
    label: `${BRACKET_LABEL[bracket]} · from ${shortStamp(since, mode)} ${zone(since, mode)}`,
    bracket,
  };
}

/**
 * The DISPLAY-ONLY scope string the server echoes back as `meta.scope`.
 *
 * Short by construction rather than by truncation: the server caps it at 24
 * characters, and "YTD · from 2026-01-01 00:00 CET" chopped at 24 reads
 * "…2026-01-01 00", which is a worse label than no label. Nothing is lost —
 * the boundary itself travels as `since`, in full, on every request.
 */
export function scopeParam(r: Range): string {
  const s =
    r.bracket === "custom"
      ? `${(r.since ?? "earliest").slice(0, 10)} → ${(r.until ?? "now").slice(0, 10)}`
      : BRACKET_LABEL[r.bracket];
  return s.slice(0, 24);
}

/** Poll cadence follows the bracket, restored from the old dashboard: a 24 h
 *  reading is a live reading, a 90-day one is a report, and an explicit past
 *  range does not move at all. */
export function refetchMsFor(r: Range): number | false {
  if (r.bracket === "custom") return false;
  if (r.bracket === "24h") return 30_000;
  return 300_000;
}

/* ----------------------------------------------------------------- store */

const KEY = "regrine.bracket.v1";

interface Persisted {
  bracket: Bracket;
  from: string | null;
  to: string | null;
}

interface Snapshot extends Persisted {
  range: Range;
}

function load(): Persisted {
  const fallback: Persisted = { bracket: DEFAULT_BRACKET, from: null, to: null };
  if (typeof sessionStorage === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return fallback;
    const v = JSON.parse(raw) as Partial<Persisted>;
    return {
      bracket: isBracket(v.bracket) ? v.bracket : DEFAULT_BRACKET,
      from: typeof v.from === "string" ? v.from : null,
      to: typeof v.to === "string" ? v.to : null,
    };
  } catch {
    return fallback;
  }
}

function save(v: Persisted): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* quota / private mode — the selection still holds for this page */
  }
}

let snap: Snapshot = (() => {
  const p = load();
  return { ...p, range: resolveBracket(p.bracket, p.from, p.to) };
})();

const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

function emit(): void {
  for (const l of listeners) l();
}

/** Re-resolve without changing the SELECTION, so a rolling preset keeps up
 *  with the clock. Silent unless the resolved lower bound actually moved. */
function retick(): void {
  const next = resolveBracket(snap.bracket, snap.from, snap.to);
  if (next.since === snap.range.since && next.until === snap.range.until && next.label === snap.range.label) return;
  snap = { ...snap, range: next };
  emit();
}

/** THE DISPLAY ZONE MOVES THE BOUNDARY, so it must move the resolved range.
 *
 *  YTD is "since Jan 1 in the zone on screen", and the LOCAL/UTC toggle is
 *  what picks that zone — an hour apart in Warsaw, a whole day for a reader
 *  who lands near midnight. Without this, flipping the toggle re-rendered
 *  and refetched with the ALREADY-RESOLVED `since`, so the figures did not
 *  move and the boundary silently disagreed with the clock beside it. */
onTzModeChange(() => retick());

function commit(p: Persisted, mirror = true): void {
  snap = { ...p, range: resolveBracket(p.bracket, p.from, p.to) };
  save(p);
  if (mirror) {
    setParams({
      b: p.bracket,
      from: p.from,
      to: p.to,
    });
  }
  emit();
}

/**
 * THE URL IS THE TRUTH ON FIRST PAINT.
 *
 * A link someone was sent, or a reload of a page whose bracket was mirrored
 * into the URL, must open on the range it names. An EMPTY url carries no
 * opinion and keeps the persisted selection — the same rule the old
 * dashboard's `hydrateFromUrl` used, and the reason a shared link is
 * reproducible.
 */
export function hydrateFromUrl(search?: string): void {
  const raw = search ?? (typeof location === "undefined" ? "" : location.search);
  let sp: URLSearchParams;
  try {
    sp = new URLSearchParams(raw);
  } catch {
    return;
  }
  const b = sp.get("b");
  const from = sp.get("from");
  const to = sp.get("to");
  if (!b && !from && !to) return;
  commit(
    {
      bracket: from || to ? "custom" : isBracket(b) ? b : DEFAULT_BRACKET,
      from: from || null,
      to: to || null,
    },
    false,
  );
}

hydrateFromUrl();

export function setBracket(b: Bracket): void {
  commit({ bracket: isBracket(b) ? b : DEFAULT_BRACKET, from: null, to: null });
}

export function setCustom(from: string | null, to: string | null): void {
  if (!from && !to) {
    setBracket(DEFAULT_BRACKET);
    return;
  }
  commit({ bracket: "custom", from: from || null, to: to || null });
}

/** datetime-local values, read in the DISPLAY zone. The control's `apply`
 *  goes through here so the zone conversion lives in one place. */
export function setCustomLocal(fromLocal: string, toLocal: string): void {
  setCustom(fromLocalInput(fromLocal), fromLocalInput(toLocal));
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  if (!ticker && typeof setInterval === "function") {
    ticker = setInterval(retick, QUANTUM_MS);
    (ticker as unknown as { unref?: () => void }).unref?.();
  }
  return () => {
    listeners.delete(l);
    if (listeners.size === 0 && ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  };
}

function getSnapshot(): Snapshot {
  return snap;
}

/** TEST SEAM. Not exported through the barrel of anything the app renders;
 *  a suite that mutates a module-level store has to be able to put it back. */
export function __resetBracket(): void {
  commit({ bracket: DEFAULT_BRACKET, from: null, to: null }, false);
}

export interface BracketApi extends Snapshot {
  setBracket(b: Bracket): void;
  setCustom(from: string | null, to: string | null): void;
  tz: string;
}

export function useBracket(): BracketApi {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...s, setBracket, setCustom, tz: displayTz() };
}

/** For consumers that only need the resolved range (query keys, params). */
export function useRange(): Range {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot).range;
}
